import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { useAuthStore } from "../stores/auth-store";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

export function ConnectAmoPage() {
  const navigate = useNavigate();
  const userJwt = useAuthStore((state) => state.userJwt);
  const setAccountToken = useAuthStore((state) => state.setAccountToken);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    async function handleSuccess() {
      if (!userJwt) return;

      try {
        const me = await api.getMe(userJwt);
        if (!me.hasAmoAccount) {
          setError("amoCRM аккаунт пока не найден. Попробуйте подключить снова.");
          return;
        }

        const { token } = await api.getAccountToken(userJwt);
        setAccountToken(token);
        navigate("/", { replace: true });
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : "Не удалось завершить подключение amoCRM";
        setError(message);
      } finally {
        setIsConnecting(false);
      }
    }

    function handleMessage(event: MessageEvent) {
      if (event.data?.type === "AI_CRM_AUTH_SUCCESS") {
        void handleSuccess();
      }
      if (event.data?.type === "AI_CRM_TOKEN" && event.data.token) {
        void handleSuccess();
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [navigate, setAccountToken, userJwt]);

  async function handleConnect() {
    if (!userJwt) {
      navigate("/login", { replace: true });
      return;
    }

    setError(null);
    setIsConnecting(true);

    try {
      const { url } = await api.getAmoInstallUrl(userJwt);
      const popup = window.open(url, "amo-oauth", "width=720,height=760");

      if (!popup) {
        setError("Браузер заблокировал popup. Разрешите всплывающие окна и повторите.");
        setIsConnecting(false);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Не удалось открыть OAuth amoCRM";
      setError(message);
      setIsConnecting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Подключите amoCRM</CardTitle>
          <CardDescription>
            Разрешите доступ к аккаунту amoCRM, чтобы настроить AI-агента
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button className="w-full" disabled={isConnecting} onClick={handleConnect}>
            <ExternalLink className="h-4 w-4" />
            {isConnecting ? "Ожидаем подтверждение..." : "Подключить amoCRM"}
          </Button>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <p className="text-xs text-muted-foreground">
            После подтверждения popup закроется автоматически, а интерфейс откроет настройки.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
